import { useUserStore } from '@/store';
import { MFDResponseCode, type Response } from '@/types/common';
import { message } from 'ant-design-vue';
import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { appUtil } from '..';

const LOGIN_STATE_CHANGED_MESSAGE = '登录状态已变化，请刷新页面后重试';

/**
 * @description: 请求拦截器
 */
function requestInterceptor(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const utoken = appUtil.getUToken();
  if (utoken) {
    config.headers['utoken'] = utoken;
  }
  return config;
}

/**
 * @description: 响应拦截器
 */
function responseInterceptor(response: AxiosResponse): Response | null {
  if (response.request.responseType === 'arraybuffer') {
    return response.data;
  }
  if (
    response.data.code === 0 ||
    response.data.code === MFDResponseCode.ErrPassportAuth || // 10100 不提示错误
    response.data.code === MFDResponseCode.ReportNotExist || // 24009(测试报告不存在) 不提示错误
    response.data.code === MFDResponseCode.ErrFeishuUnAuth || // 91001(飞书未授权) 不提示错误
    response.data instanceof Blob
  ) {
    return response.data;
  } else if (response.data.code === 10005) {
    appUtil.rmUToken();
    appUtil.goToLoginPage();
    message.error('Token 失效，请重新登录');
    return null;
  } else if (response.data.code === 10004) {
    message.error('当前用户无权限');
    return null;
  } else {
    response.data.msg?.length > 0 && message.error(response.data.msg);
    return response.data;
  }
}

function getRequestUToken(error: AxiosError): string | null {
  const headers = error.config?.headers as any;
  if (!headers) {
    return null;
  }
  if (typeof headers.get === 'function') {
    return headers.get('utoken') || headers.get('UToken') || null;
  }
  return headers.utoken || headers.UToken || headers['utoken'] || null;
}

function isStaleUnauthorized(error: AxiosError) {
  const requestToken = getRequestUToken(error);
  const currentToken = appUtil.getUToken();
  return !!requestToken && !!currentToken && requestToken !== currentToken;
}

function isCanceledRequest(error: AxiosError) {
  return error.code === 'ERR_CANCELED' || error.message === 'canceled';
}

/**
 * @desc: 异常拦截处理器
 */
function errorHandler(error: AxiosError): Promise<never> {
  if (isCanceledRequest(error)) {
    return Promise.reject(error);
  }

  if (error.response) {
    if (error.response.status === 401) {
      if (isStaleUnauthorized(error)) {
        message.warning(LOGIN_STATE_CHANGED_MESSAGE);
        return Promise.reject(error);
      }
      message.warning(LOGIN_STATE_CHANGED_MESSAGE);
      const userStore = useUserStore();
      userStore.logout();
      return Promise.reject(error);
    }

    const errorData = error.response.data as any;
    let errorMessage = '';

    if (errorData?.message && typeof errorData.message === 'string') {
      errorMessage = errorData.message;
    } else if (errorData?.msg && typeof errorData.msg === 'string') {
      errorMessage = errorData.msg;
    } else {
      const statusMessages: Record<number, string> = {
        400: '请求参数错误',
        404: '资源不存在',
        500: '服务器内部错误',
      };
      errorMessage = statusMessages[error.response.status] || `请求失败 (${error.response.status})`;
    }

    if (errorMessage) {
      message.error(errorMessage);
    }
  } else {
    error.message?.length > 0 && message.error(error.message);
  }

  return Promise.reject(error);
}

export default {
  requestInterceptor,
  responseInterceptor,
  requestErrorHandler: errorHandler,
  responseErrorHandler: errorHandler,
};
